import { useEffect } from "react";
import { Check, Pencil, Target, X } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { fmtNum } from "./utils";

/**
 * Inline tile that shows a big total, a gold progress bar toward the user's daily goal,
 * and an editable goal (pencil → input + save). Fires a one-time-per-day toast when the
 * goal is first crossed on this device.
 */
export default function GoalTile({
  testid, Icon, label, unitLabel, unitShort, value, goal, editing, setEditing,
  goalDraft, setGoalDraft, onSaveGoal, onClearGoal, celebrateKey, dayISO,
}) {
  const goalSet = goal != null && Number(goal) > 0;
  const pct = goalSet ? Math.min(100, (value / Number(goal)) * 100) : 0;
  const reached = goalSet && value >= Number(goal);

  // One-time celebration toast per day per tile
  useEffect(() => {
    if (!reached) return;
    try {
      const key = `amino_${celebrateKey}_hit_${dayISO}`;
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, "1");
        toast.success(`🎯 ${label} goal hit — ${fmtNum(value)} / ${fmtNum(goal)} ${unitShort}`, { duration: 4500 });
      }
    } catch { /* ignore */ }
  }, [reached, value, goal, label, unitShort, celebrateKey, dayISO]);

  return (
    <div
      className={[
        "relative overflow-hidden border p-4 bg-slate-50 transition-colors",
        reached ? "border-[#0D9488]" : "border-slate-200",
      ].join(" ")}
      data-testid={testid}
    >
      {/* Progress fill */}
      {goalSet && (
        <div
          aria-hidden
          className={[
            "absolute inset-y-0 left-0 pointer-events-none transition-all duration-700 ease-out",
            reached ? "bg-[#0D9488]/25" : "bg-[#0D9488]/10",
          ].join(" ")}
          style={{ width: `${pct}%` }}
          data-testid={`${testid}-progress-fill`}
        />
      )}

      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500">
            <Icon className="w-3.5 h-3.5 text-[#0D9488]" /> {label}
          </div>
          {reached && (
            <span
              className="font-mono-num text-[10px] uppercase tracking-widest text-[#0D9488] flex items-center gap-1 animate-pulse"
              data-testid={`${testid}-goal-hit`}
            >
              <Check className="w-3 h-3" /> Goal hit
            </span>
          )}
        </div>

        <div className="font-display font-black text-4xl leading-none mt-2 text-slate-900 dark:text-white tabular-nums">
          {fmtNum(value)}
          {goalSet && (
            <span className="text-slate-400 text-lg font-normal ml-1 tabular-nums">
              / {fmtNum(goal)}
            </span>
          )}
        </div>

        {/* Goal row */}
        <div className="mt-3 flex items-center justify-between gap-2">
          <div className="font-mono-num text-[10px] text-slate-400 uppercase tracking-widest">
            {unitLabel} · {goalSet ? `${Math.round(pct)}% of goal` : "no goal set"}
          </div>
          {editing ? (
            <div className="flex items-center gap-1">
              <Input
                type="number"
                min="0"
                step="1"
                autoFocus
                value={goalDraft}
                onChange={(e) => setGoalDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") onSaveGoal(); if (e.key === "Escape") setEditing(false); }}
                placeholder="Target"
                className="w-24 bg-slate-50 border-slate-300 rounded-none h-7 font-mono-num text-xs"
                data-testid={`${testid}-goal-input`}
              />
              <Button size="icon" onClick={onSaveGoal} className="h-7 w-7 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none" data-testid={`${testid}-goal-save`}>
                <Check className="w-3.5 h-3.5" />
              </Button>
              {goalSet && (
                <button
                  type="button"
                  onClick={onClearGoal}
                  className="h-7 px-1.5 text-[10px] font-mono-num uppercase tracking-widest text-slate-500 hover:text-[#FF3B30] border border-slate-200 hover:border-[#FF3B30] transition-colors"
                  data-testid={`${testid}-goal-clear`}
                  title="Clear goal"
                >
                  Clear
                </button>
              )}
              <Button size="icon" variant="outline" onClick={() => setEditing(false)} className="h-7 w-7 border-slate-200 bg-transparent hover:bg-slate-50 rounded-none">
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { setGoalDraft(goalSet ? String(goal) : ""); setEditing(true); }}
              className="inline-flex items-center gap-1.5 font-mono-num text-[10px] uppercase tracking-widest text-slate-500 hover:text-[#0D9488] transition-colors border border-slate-200 hover:border-[#0D9488] px-2 py-1"
              data-testid={`${testid}-goal-edit`}
            >
              {goalSet ? <Pencil className="w-3 h-3" /> : <Target className="w-3 h-3" />}
              {goalSet ? "Edit goal" : "Set goal"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
